// The two behaviours every entity form shares, wherever it sits: a close that
// asks before throwing an edit away, and a delete that confirms first, naming
// the row. EntityFormModal and EntityFormPanel both take them from here.

import type { ReactNode } from "react";
import type { EntityForm } from "@/platform/client";
import { useConfirm } from "@/platform/ui/patterns/ConfirmProvider.js";
import { useConfirmedClose } from "@/platform/ui/patterns/useConfirmedClose.js";

export function useEntityFormActions<T, F>(
  form: EntityForm<T, F>,
  noun: string,
  describe?: (editing: T) => ReactNode,
): { close: () => Promise<boolean>; askDelete: () => Promise<void> } {
  const confirm = useConfirm();
  const { editing, remove, requestClose, discard } = form;
  const close = useConfirmedClose(requestClose, discard, `this ${noun}`);

  async function askDelete() {
    if (!remove || !editing) return;
    await confirm({
      title: `Delete ${noun}`,
      message: describe ? (
        <>Delete {describe(editing)}? This can’t be undone.</>
      ) : (
        `Delete this ${noun}? This can’t be undone.`
      ),
      action: remove,
    });
  }

  return { close, askDelete };
}
